import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  useMediaQuery,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Logout
} from '@mui/icons-material';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { logout } from '../store/slices/authSlice';
import SidebarNavigation from './SidebarNavigation';

// Mapping of irregularity types to display names
const IRREGULARITY_DISPLAY_NAMES: Record<string, string> = {
  'sole-source': 'Sole Source Awards Investigation',
  'low-competition': 'Low Competition Analysis',
  'excessive-pricing': 'Excessive Pricing Detection',
  'split-purchase': 'Split Purchase Order Detection',
  'vendor-concentration': 'Vendor Concentration Analysis',
  'repeat-awards': 'Repeat Awards Investigation',
  'timing-anomaly': 'Timing Anomaly Detection',
  'emergency-misuse': 'Emergency Misuse Investigation'
};

const IRREGULARITY_DESCRIPTIONS: Record<string, string> = {
  'sole-source': 'Find contracts awarded without competition where competitors existed. Reveals favoritism or bypassed procurement rules.',
  'low-competition': 'Identify contracts with limited bidders (2-3 competitors). Detects restricted competition that may indicate collusion.',
  'excessive-pricing': 'Find items priced significantly above market rates. Identifies potential price gouging or fraud.',
  'split-purchase': 'Detect purchase orders split to avoid approval thresholds. Uncovers attempts to bypass oversight.',
  'vendor-concentration': 'Identify vendors receiving multiple contracts in specific areas. Detects potential monopolistic practices.',
  'repeat-awards': 'Find patterns of repeated awards to the same vendors. Identifies potential favoritism or lack of competition.',
  'timing-anomaly': 'Detect suspicious timing patterns in contract awards. Identifies rushed or manipulated procurement timelines.',
  'emergency-misuse': 'Find non-emergency items procured under emergency justifications. Identifies bypass of normal procurement rules.'
};

const SIDEBAR_WIDTH = 280;
const SIDEBAR_WIDTH_COLLAPSED = 64;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Get initial state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(anchorEl);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await dispatch(logout());
    navigate('/login');
  };

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Calculate current sidebar width
  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  // Extract current irregularity type from URL
  const getCurrentType = () => {
    const match = location.pathname.match(/\/investigator\/(.+)/);
    return match ? match[1] : null;
  };

  const currentType = getCurrentType();

  // Generate breadcrumbs
  const generateBreadcrumbs = () => {
    const crumbs = [
      <Link
        key="home"
        component={RouterLink}
        to="/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': { color: 'primary.main' }
        }}
      >
        <HomeIcon sx={{ mr: 0.5, fontSize: 'inherit' }} />
        Home
      </Link>
    ];

    if (location.pathname.startsWith('/investigator') || location.pathname.startsWith('/vendor')) {
      crumbs.push(
        <Link
          key="investigator"
          component={RouterLink}
          to="/investigator"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': { color: 'primary.main' }
          }}
        >
          Search Investigator
        </Link>
      );

      if (currentType) {
        crumbs.push(
          <Typography key={currentType} color="text.primary">
            {IRREGULARITY_DISPLAY_NAMES[currentType] || currentType}
          </Typography>
        );
      } else if (location.pathname === '/vendor/verification') {
        crumbs.push(
          <Typography key="vendor-verification" color="text.primary">
            Vendor Verification
          </Typography>
        );
      }
    }

    return crumbs;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentSidebarWidth}px)` },
          ml: { md: `${currentSidebarWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
          borderBottom: 1,
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop Sidebar Toggle */}
          <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={handleSidebarToggle}
              sx={{ 
                mr: 2, 
                display: { xs: 'none', md: 'flex' },
                transition: 'all 0.2s ease'
              }}
            >
              {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
          
          {/* Current Page Title and Breadcrumbs */}
          <Box sx={{ flexGrow: 1 }}>
            {currentType || location.pathname.startsWith('/vendor') ? (
              <Box>
                <Typography variant="h6" noWrap component="div" sx={{ mb: 0.5 }}>
                  {location.pathname === '/vendor/verification'
                    ? 'Vendor Verification Analysis'
                    : ((currentType && IRREGULARITY_DISPLAY_NAMES[currentType]) || 'Search Investigator')}
                </Typography>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {generateBreadcrumbs()}
                </Breadcrumbs>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" noWrap component="div">
                  Search Investigator
                </Typography>
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {generateBreadcrumbs()}
                </Breadcrumbs>
              </Box>
            )}
          </Box>

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user && (
              <>
                <Chip
                  label={user.role.toUpperCase()}
                  size="small"
                  color={user.role === 'admin' ? 'warning' : 'default'}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                />
                <Tooltip title="Account">
                  <IconButton onClick={handleUserMenuOpen} size="small">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Toolbar>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={userMenuOpen}
          onClose={handleUserMenuClose}
          PaperProps={{ sx: { minWidth: 200, mt: 1.5 } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>{user?.username}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><Logout fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </AppBar>

      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{ width: { md: currentSidebarWidth }, flexShrink: { md: 0 } }}
        aria-label="investigation types"
      >
        {/* Mobile drawer */}
        {isMobile && (
          <SidebarNavigation
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            width={SIDEBAR_WIDTH}
          />
        )}
        
        {/* Desktop permanent drawer */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <SidebarNavigation
            variant="permanent"
            open={true}
            width={currentSidebarWidth}
            collapsed={sidebarCollapsed}
          />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${currentSidebarWidth}px)` },
          ml: { xs: 0, md: 0 },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Toolbar spacer */}
        <Toolbar />

        {/* Page Description for Investigator Pages */}
        {currentType && IRREGULARITY_DESCRIPTIONS[currentType] && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: 'primary.light',
              color: 'white',
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <Typography variant="body1">
              {IRREGULARITY_DESCRIPTIONS[currentType]}
            </Typography>
          </Box>
        )}

        {/* Page Content */}
        <Box sx={{ mt: currentType ? 2 : 0, flexGrow: 1 }}>
          {children}
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            mt: 'auto',
            py: 2,
            px: 3,
            textAlign: 'center',
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Patent Pending · MatchAwards.com
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;